// src/components/SignupPage.js - Split-screen desktop-first layout
//
// Invite-only sign-up (PRD_SignupInvite C1, C3, C10). The page opens only from a
// one-time link (/signup?invite=<token>) sent from the platform app. On load it
// checks the link and pre-fills the invited email (locked), name and company. On
// submit the server (redeemSignupInvite) creates the whole account — login,
// claims, company, owner record, default settings, verification email — and the
// page only signs in; useAuth then opens /verify-email.
import { useState, useEffect, useRef } from "react";
import { Navigate } from "react-router-dom";
import { signInWithEmailAndPassword } from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import { auth } from "../firebase/config";
import { SIGNUP_NOTICE_KEY } from "../hooks/useAuth";
import { generateSlug, validateSlug } from "../utils/validation";
import { LEGAL_VERSIONS } from "../config/legal";
import { AuthBrandingPanel, AuthMobileHeader } from "./auth/AuthBrandingPanel";
import { LoadingScreen } from "./LoadingScreen";

// Initialize Firebase Functions with asia-southeast1 region
const functions = getFunctions(undefined, "asia-southeast1");

export const INVALID_LINK_MESSAGE =
  "This sign-up link is no longer valid. Contact discovery@sahod.solutions for a new one.";
export const CHECK_FAILED_MESSAGE = "We couldn't check your link. Please refresh the page.";
export const RETRY_MESSAGE = "Something went wrong. Please try again.";
export const UNKNOWN_OUTCOME_MESSAGE =
  "If your account was created, sign in at sahod.solutions/login. Otherwise contact discovery@sahod.solutions.";
export const ACCOUNT_READY_MESSAGE = "Your account is ready. Please sign in.";
const WEAK_PASSWORD_MESSAGE = "Password is too weak. Please use at least 8 characters.";

const readInviteToken = () => new URLSearchParams(window.location.search).get("invite");

export const SignupPage = () => {
  const [token] = useState(readInviteToken);
  // checking | form | invalid | checkFailed | accountReady
  const [view, setView] = useState("checking");
  const [formData, setFormData] = useState({
    companyName: "",
    slug: "",
    fullName: "",
    email: "",
    password: "",
    confirmPassword: "",
    termsAccepted: false,
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [slugChecking, setSlugChecking] = useState(false);
  const redeeming = useRef(false);

  // Surface validation/submit errors: the error banner is at the top of the form,
  // so scroll up to it whenever one appears (e.g. after clicking Create Account
  // while scrolled down at the consent checkbox).
  useEffect(() => {
    if (error) window.scrollTo({ top: 0, behavior: "smooth" });
  }, [error]);

  // Check the link once on load and pre-fill the form from the invite.
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await httpsCallable(functions, "checkSignupInvite")({ token });
        if (cancelled) return;
        setFormData((prev) => ({ ...prev, email: data.email, fullName: data.fullName }));
        setView("form");
        handleCompanyNameChange(data.companyName);
      } catch (err) {
        if (cancelled) return;
        setView(err.code === "functions/failed-precondition" ? "invalid" : "checkFailed");
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- runs once per token
  }, [token]);

  // Auto-generate slug when company name changes
  const handleCompanyNameChange = async (companyName) => {
    setFormData((prev) => ({ ...prev, companyName }));

    if (companyName.length > 2) {
      const generatedSlug = generateSlug(companyName);
      setFormData((prev) => ({ ...prev, slug: generatedSlug }));
      await checkSlugAvailability(generatedSlug);
    }
  };

  // Check if slug is available via Cloud Function
  const checkSlugAvailability = async (slug) => {
    if (!slug) return;

    setSlugChecking(true);
    setError("");

    try {
      const checkSlug = httpsCallable(functions, "checkSlugAvailable");
      const result = await checkSlug({ slug });

      if (!result.data.available) {
        const suggestions = result.data.suggestions || [];
        const suggestionText =
          suggestions.length > 0
            ? ` Try: ${suggestions.slice(0, 3).join(", ")}`
            : "";

        if (result.data.reason === "reserved") {
          setError(`"${slug}" is a reserved name.${suggestionText}`);
        } else {
          setError(`"${slug}" is already taken.${suggestionText}`);
        }
      }
    } catch (err) {
      console.error("Error checking slug:", err);
      // Show user-friendly error
      if (err.code === "functions/invalid-argument") {
        setError(err.message);
      } else {
        setError("Unable to check availability. Please try again.");
      }
    } finally {
      setSlugChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (redeeming.current) return;
    setLoading(true);
    setError("");

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError(WEAK_PASSWORD_MESSAGE);
      setLoading(false);
      return;
    }

    if (!validateSlug(formData.slug)) {
      setError("Invalid company slug. Use letters, numbers, and hyphens only.");
      setLoading(false);
      return;
    }

    if (!formData.termsAccepted) {
      setError("Please accept the terms and conditions");
      setLoading(false);
      return;
    }

    // The server creates the whole account (C1). Called once per submit.
    redeeming.current = true;
    let result;
    try {
      result = await httpsCallable(functions, "redeemSignupInvite")({
        token,
        password: formData.password,
        fullName: formData.fullName,
        companyName: formData.companyName,
        slug: formData.slug,
        legalVersions: LEGAL_VERSIONS,
      });
    } catch (err) {
      redeeming.current = false;
      setLoading(false);
      if (err.code === "functions/failed-precondition") {
        setView("invalid");
      } else if (err.code === "functions/invalid-argument" || err.code === "functions/already-exists") {
        setError(err.message);
      } else if (err.code === "functions/internal") {
        setError(RETRY_MESSAGE);
      } else {
        // A lost response: the account may already exist.
        setError(UNKNOWN_OUTCOME_MESSAGE);
      }
      return;
    }

    // E4: a failed verification send never fails the sign-up — VerifyEmailPage
    // shows this notice and points at its Resend button.
    if (!result.data.verificationSent) {
      try {
        sessionStorage.setItem(
          SIGNUP_NOTICE_KEY,
          "Account created, but we couldn't send your verification email. Tap Resend below."
        );
      } catch (storageErr) {
        // Site data blocked — the user just sees the normal page and can
        // still use Resend.
      }
    }

    try {
      // useAuth takes over from here and opens /verify-email.
      await signInWithEmailAndPassword(auth, result.data.email, formData.password);
    } catch (signInErr) {
      console.error("Sign-in after sign-up failed:", signInErr.code);
      setLoading(false);
      setView("accountReady");
    }
  };

  if (!token) return <Navigate to="/" replace />;
  if (view === "checking") return <LoadingScreen message="Checking your sign-up link..." />;
  if (view !== "form") {
    const message =
      view === "invalid" ? INVALID_LINK_MESSAGE
        : view === "checkFailed" ? CHECK_FAILED_MESSAGE
          : ACCOUNT_READY_MESSAGE;
    const link = view === "accountReady" ? { href: "/login", label: "Sign in" } : { href: "/", label: "Go to sahod.solutions" };
    return (
      <div className="auth-container">
        <AuthBrandingPanel />
        <div className="auth-form-panel">
          <div className="auth-form-container">
            <AuthMobileHeader />
            <div className="auth-form-header">
              <p className="auth-form-subtitle">{message}</p>
            </div>
            <div className="auth-form-footer">
              <a href={link.href} className="btn btn--link">
                {link.label}
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-container">
      {/* Left Branding Panel (Desktop Only) - Sticky for tall form */}
      <AuthBrandingPanel sticky />

      {/* Right Form Panel */}
      <div className="auth-form-panel">
        <div className="auth-form-container auth-form-container--wide">
          <AuthMobileHeader />

          <div className="auth-form-header">
            <h2 className="auth-form-title">Create your company account</h2>
            <p className="auth-form-subtitle">Set up your company in minutes</p>
          </div>

          <form onSubmit={handleSubmit} className="form">
            {/* Error Alert */}
            {error && (
              <div className="alert alert--error mb-6">
                <div className="alert__content">{error}</div>
              </div>
            )}

            {/* Company Information Section */}
            <div className="auth-form-section">
              <h3 className="auth-form-section-title">Company Information</h3>

              <div className="form-group">
                <label className="form-label form-label--required">
                  Company Name
                </label>
                <input
                  type="text"
                  required
                  maxLength={100}
                  value={formData.companyName}
                  onChange={(e) => handleCompanyNameChange(e.target.value)}
                  className="form-input"
                  placeholder="ABC Manufacturing Ltd."
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  Company Slug
                </label>
                <div className="input-group">
                  <span className="input-group__addon input-group__addon--prepend">
                    /company/
                  </span>
                  <input
                    type="text"
                    required
                    value={formData.slug}
                    onChange={(e) => {
                      const slug = e.target.value
                        .toLowerCase()
                        .replace(/[^a-z0-9-]/g, "");
                      setFormData((prev) => ({ ...prev, slug }));
                      checkSlugAvailability(slug);
                    }}
                    className="form-input"
                    placeholder="abc-manufacturing"
                  />
                </div>
                <div className="form-help">
                  Your company dashboard: sahod.solutions/company/
                  {formData.slug}/dashboard
                </div>
                {slugChecking && (
                  <div className="form-message form-message--info">
                    Checking availability...
                  </div>
                )}
              </div>
            </div>

            {/* Administrator Account Section */}
            <div className="auth-form-section">
              <h3 className="auth-form-section-title">Administrator Account</h3>

              <div className="form-group">
                <label className="form-label form-label--required">
                  Full Name
                </label>
                <input
                  type="text"
                  required
                  maxLength={100}
                  value={formData.fullName}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      fullName: e.target.value,
                    }))
                  }
                  className="form-input"
                  placeholder="Maria Santos"
                />
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  Email Address
                </label>
                <input
                  type="text"
                  inputMode="email"
                  autoComplete="email"
                  readOnly
                  value={formData.email}
                  className="form-input form-input--locked"
                  placeholder="maria@abcmanufacturing.com"
                />
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  Password
                </label>
                <input
                  type="password"
                  required
                  value={formData.password}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  className="form-input"
                  placeholder="Minimum 8 characters"
                />
              </div>

              <div className="form-group">
                <label className="form-label form-label--required">
                  Confirm Password
                </label>
                <input
                  type="password"
                  required
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      confirmPassword: e.target.value,
                    }))
                  }
                  className="form-input"
                  placeholder="Re-enter your password"
                />
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="form-group form-consent">
              <input
                id="terms"
                type="checkbox"
                checked={formData.termsAccepted}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    termsAccepted: e.target.checked,
                  }))
                }
                className="form-checkbox form-consent__box"
              />
              <label htmlFor="terms" className="form-consent__label">
                I agree to the{" "}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                </a>{" "}
                and{" "}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                </a>
              </label>
            </div>

            {/* Submit Button */}
            <div className="form-group">
              <button
                type="submit"
                disabled={loading || slugChecking}
                className={`btn btn--ph-primary btn--full btn--md ${
                  loading ? "btn--loading" : ""
                }`}
              >
                {loading ? "Creating Account..." : "Create Account"}
              </button>
            </div>
          </form>

          {/* Divider */}
          <div className="form-divider">
            <span className="form-divider__text">Already have an account?</span>
          </div>

          {/* Login Link */}
          <div className="auth-form-footer">
            <a href="/login" className="btn btn--link">
              Sign in here
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};
